<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { toProjectPage } from '~/utilities/to-project-page';
import { useBadgesStore } from '~/stores/badges-store';
import { useUserStore } from '~/stores/user-store';
import Spinner from '~/components/Spinner.vue';
import SvgFoundersBadge from '~/components/svg/SvgFoundersBadge.vue';
import { createNewProject } from '~/utilities/create-new-project';
import { useWhatsNewNotification } from '~/composables/whats-new-notification';
import IconAdd from '~icons/carbon/add-filled';
import IconHome from '~icons/mdi/home';
import IconLightbulb from '~icons/ph/lightbulb-filament-fill';
import IconBook from '~icons/fa/book';
import IconStar from '~icons/carbon/star-filled';

const { t } = useI18n();
const router = useRouter();
const userStore = useUserStore();
const badgesStore = useBadgesStore();
const { hasViewedWhatsNew, conditionalHash } = useWhatsNewNotification();

const isCreatingNewProject = ref(false);

async function createProjectAndNavigate() {
  isCreatingNewProject.value = true;

  try {
    const newProject = await createNewProject();
    await router.push(toProjectPage(newProject));
  } catch (error) {
  } finally {
    isCreatingNewProject.value = false;
  }
}

const tooltipConfig = {
  distance: 8,
  placement: 'right',
  delay: {
    show: 0,
  },
};
</script>

<template>
  <div v-if="userStore.isLoggedIn" class="rail-width hidden shrink-0 md:block">
    <nav
      class="custom-scrollbar rail-width fixed left-0 right-0 flex h-screen flex-col items-center gap-3 overflow-auto border-r border-new-gray-100/70 bg-new-content-100 pb-4 pt-2 dark:border-new-gray-950/70 dark:bg-new-slate-700"
    >
      <router-link class="mb-2 p-2" :to="{ name: 'dashboard' }" :title="t('links.dashboard')">
        <svg class="h-[32px] w-[35px]">
          <use xlink:href="/images/pyscript-logo-icon.svg#pyscript-logo-icon" />
        </svg>
      </router-link>

      <button
        v-tooltip="{
          ...tooltipConfig,
          content: isCreatingNewProject ? t('buttons.creating_project') : t('buttons.new_project'),
        }"
        class="nav-item"
        :aria-label="
          isCreatingNewProject ? t('buttons.creating_project') : t('buttons.new_project')
        "
        :disabled="isCreatingNewProject"
        data-testid="new-project-button"
        @click="createProjectAndNavigate"
      >
        <div class="flex h-5 w-5 items-center justify-center">
          <Spinner v-if="isCreatingNewProject" class="h-4 w-4 flex-none" aria-hidden="true" />
          <IconAdd v-else class="shrink-0 text-[18px]" aria-hidden="true" />
        </div>
      </button>

      <router-link
        v-tooltip="{
          ...tooltipConfig,
          content: t('links.dashboard'),
        }"
        :to="{ name: 'dashboard' }"
        class="nav-item"
        :aria-label="t('links.dashboard')"
      >
        <IconHome class="text-[20px]" />
      </router-link>

      <router-link
        v-tooltip="{
          ...tooltipConfig,
          content: t('links.favorites'),
        }"
        class="nav-item"
        :to="{ name: 'favorites' }"
        :title="t('links.favorites')"
      >
        <IconStar class="text-[18px]" />
      </router-link>

      <hr class="mt-auto" />

      <router-link
        v-tooltip="{
          ...tooltipConfig,
          content: t('links.docs'),
        }"
        class="nav-item"
        :to="{ name: 'docs' }"
        :title="t('links.docs')"
      >
        <IconBook class="text-[18px]" />
      </router-link>

      <router-link
        v-tooltip="{
          ...tooltipConfig,
          content: t('links.whats_new'),
        }"
        class="nav-item"
        :to="{ name: 'whats-new', hash: conditionalHash }"
        :title="t('links.whats_new')"
      >
        <span class="relative">
          <IconLightbulb class="text-[20px]" />
          <span
            v-if="!hasViewedWhatsNew"
            class="absolute right-0 top-0 -mr-2 -mt-1 flex h-2.5 w-2.5"
          >
            <span
              class="absolute inline-flex h-full w-full animate-ping rounded-full bg-new-green-600 opacity-75"
            />
            <span class="relative inline-flex h-2.5 w-2.5 rounded-full bg-new-green-700" />
          </span>
        </span>
      </router-link>

      <div class="shrink-0 pt-1 text-4xl" aria-hidden="true">
        <SvgFoundersBadge v-if="badgesStore.hasFoundersBadge" />
      </div>
    </nav>
  </div>
</template>

<style scoped lang="postcss">
.rail-width {
  @apply w-16;
}

.nav-item {
  @apply flex w-full items-center justify-center rounded p-2 transition-colors hover:text-new-green-700;
}
</style>
